import {useNoIndex} from 'hooks/noIndex';
import {useEffect, useState} from 'react';
import {exchangeShareLink, readShareTokenFromHash} from 'services/shareSession';

/** Where a minted link points: `/share#t={token}`. */
export const SHARE_PATH = '/share';

export type ShareLinkState =
  | {status: 'none'}
  | {status: 'pending'}
  | {status: 'ready'; targetUrl: string}
  | {status: 'failed'};

/**
 * Returns the shared page's URL so the caller renders it *as* the current route, above
 * the router because React Router only accepts an override where the base is `/`.
 * Holds only while shared pages are leaves: a `navigate()` inside one pushes a route
 * with no `#t=`, leaving a reload nothing to renew from.
 */
export function useShareLinkTarget(): ShareLinkState {
  const isSharePath = window.location?.pathname === SHARE_PATH;
  const [state, setState] = useState<ShareLinkState>(() =>
    isSharePath ? {status: 'pending'} : {status: 'none'},
  );

  useEffect(() => {
    if (!isSharePath) return;

    const token = readShareTokenFromHash(window.location.hash);

    if (!token) {
      setState({status: 'failed'});
      return;
    }

    let cancelled = false;

    exchangeShareLink(token)
      .then(({target_url}) => {
        if (!cancelled) setState({status: 'ready', targetUrl: target_url});
      })
      .catch(() => {
        if (!cancelled) setState({status: 'failed'});
      });

    return () => {
      cancelled = true;
    };
  }, [isSharePath]);

  return state;
}

export const ShareLinkPending = () => {
  useNoIndex();

  return (
    <div className="h-screen flex items-center justify-center">
      <p className="text-sm text-neutral-500">Opening the shared view…</p>
    </div>
  );
};

/** One message for every failure: telling them apart rebuilds the oracle the API avoids. */
export const ShareLinkUnavailable = () => {
  useNoIndex();

  return (
    <div className="h-screen flex flex-col items-center justify-center gap-2 px-6 text-center">
      <h1 className="text-lg font-semibold">This link doesn’t work anymore</h1>
      <p className="text-sm text-neutral-500">
        It may have expired or been turned off. Ask whoever shared it for a new
        one.
      </p>
    </div>
  );
};
